import React, { useRef } from 'react';
import { ProjectForm, Specification, LegalDocument, DynamicSlot, FAQ, NearbyPlace } from './types';
import { toast } from 'react-toastify';
import { getImageUrl, API_BASE } from '../../../utils/api';
import { generateStep8SampleExcel, parseStep8Excel } from '../../../utils/excelTemplate';
import IconLibraryModal from '../../../components/admin/IconLibraryModal';
import { NEARBY_CATEGORY_ICONS } from './constants';

export interface Step8GridProps {
    form: ProjectForm;
    setForm: React.Dispatch<React.SetStateAction<ProjectForm>>;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    errors?: Record<string, boolean>;
    selectedAmenities: string[];
    setSelectedAmenities: React.Dispatch<React.SetStateAction<string[]>>;
    globalAmenities: { _id: string, name: string, icon: string, score: number }[];
    specifications: Specification[];
    setSpecifications: React.Dispatch<React.SetStateAction<Specification[]>>;
    legalDocuments: LegalDocument[];
    setLegalDocuments: React.Dispatch<React.SetStateAction<LegalDocument[]>>;
    globalLandmarkCategories?: { _id: string, name: string, icon: string }[];
    scoreSettings?: any;
}

const Step8Grid: React.FC<Step8GridProps> = ({
    form,
    setForm,
    handleChange,
    errors,
    selectedAmenities,
    setSelectedAmenities,
    globalAmenities,
    specifications,
    setSpecifications,
    legalDocuments,
    setLegalDocuments,
    globalLandmarkCategories = [],
    scoreSettings
}) => {

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isLibraryOpen, setIsLibraryOpen] = React.useState(false);
    const availableCategories = Array.from(new Set([
        ...globalLandmarkCategories.map(c => c.name),
        ...Object.keys(NEARBY_CATEGORY_ICONS)
    ]));
    const [libraryTarget, setLibraryTarget] = React.useState<{type: 'highlight' | 'customAmenity' | 'specification', index: number} | null>(null);

    const handleLibrarySelect = (iconUrl: string) => {
        if (!libraryTarget) return;
        
        if (libraryTarget.type === 'highlight') {
            const n = [...form.highlights];
            n[libraryTarget.index].icon = iconUrl;
            n[libraryTarget.index].iconPreview = undefined;
            n[libraryTarget.index].file = undefined;
            setForm(prev => ({ ...prev, highlights: n }));
        } else if (libraryTarget.type === 'customAmenity') {
            const newCa = [...(form.customAmenities || [])];
            newCa[libraryTarget.index] = { ...newCa[libraryTarget.index], icon: iconUrl, iconPreview: undefined, file: undefined };
            setForm(prev => ({ ...prev, customAmenities: newCa }));
        } else if (libraryTarget.type === 'specification') {
            const n = [...specifications];
            n[libraryTarget.index].icon = iconUrl;
            n[libraryTarget.index].iconPreview = undefined;
            n[libraryTarget.index].file = undefined;
            setSpecifications(n);
        }
    };

    const handleDownloadTemplate = () => {
        generateStep8SampleExcel();
    };

    const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const data = await parseStep8Excel(file);
            
            // Update form with imported data
            let msg = [];
            if (data.highlights && data.highlights.length > 0) {
                setForm(prev => ({ ...prev, highlights: data.highlights }));
                msg.push(`${data.highlights.length} Highlights`);
            }
            if (data.nearbyPlaces && data.nearbyPlaces.length > 0) {
                setForm(prev => ({ ...prev, nearbyPlaces: data.nearbyPlaces }));
                msg.push(`${data.nearbyPlaces.length} Landmarks`);
            }
            if (data.amenities && data.amenities.length > 0) {
                const newAmenityIds: string[] = [];
                const newCustomAmenities: any[] = [];
                
                data.amenities.forEach((name: string) => {
                    const found = globalAmenities.find(a => a.name.toLowerCase() === name.toLowerCase());
                    if (found) {
                        newAmenityIds.push(found._id);
                    } else {
                        newCustomAmenities.push({ name: name, icon: '' });
                    }
                });
                
                if (newAmenityIds.length > 0) {
                    setSelectedAmenities(prev => Array.from(new Set([...prev, ...newAmenityIds])));
                    msg.push(`${newAmenityIds.length} Global Amenities`);
                }
                
                if (newCustomAmenities.length > 0) {
                    setForm(prev => ({ 
                        ...prev, 
                        customAmenities: [...(prev.customAmenities || []), ...newCustomAmenities] 
                    }));
                    msg.push(`${newCustomAmenities.length} Custom Amenities`);
                }
            }
            if (data.specifications && data.specifications.length > 0) {
                setSpecifications(data.specifications);
                msg.push(`${data.specifications.length} Specifications`);
            }
            if (data.faq && data.faq.length > 0) {
                setForm(prev => ({ ...prev, faq: data.faq }));
                msg.push(`${data.faq.length} FAQs`);
            }
            if (data.disclaimer) {
                setForm(prev => ({ ...prev, disclaimer: data.disclaimer }));
                msg.push(`Disclaimer Text`);
            }
            if (data.legalDocuments && data.legalDocuments.length > 0) {
                setLegalDocuments(data.legalDocuments);
                msg.push(`${data.legalDocuments.length} Legal Documents`);
            }
            if (data.dynamicSlots && data.dynamicSlots.length > 0) {
                setForm(prev => ({ ...prev, dynamicSlots: data.dynamicSlots }));
                msg.push(`${data.dynamicSlots.length} Dynamic Slots`);
            }

            if (msg.length > 0) {
                toast.success(`Imported: ${msg.join(', ')}`);
            } else {
                toast.info("No valid data found to import from the Excel file.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to parse Excel file.");
        }
        
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // --- Card 1: Key Highlights ---
    const highlightsText = form.highlights.map(h => h.text).join('\n');
    const handleHighlightsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const lines = e.target.value.split('\n');
        const newHighlights = lines.map(line => ({ icon: 'fa-star', text: line }));
        setForm(prev => ({ ...prev, highlights: newHighlights }));
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">
                    8
                </div>
                <div>
                    <h2 className="text-xl font-bold text-[#05144b]">Highlights & Details</h2>
                    <p className="text-sm text-slate-500">Add key Highlights, nearby landmarks, amenities, specifications, FAQ and disclaimer</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <button type="button" onClick={handleDownloadTemplate} className="px-3 py-2 bg-emerald-50 text-emerald-600 text-sm font-bold rounded-lg flex items-center gap-2 border border-emerald-100 hover:bg-emerald-100 transition-colors">
                        <i className="fa-solid fa-file-excel"></i> Template
                    </button>
                    <input type="file" accept=".xlsx, .xls, .csv" className="hidden" ref={fileInputRef} onChange={handleFileImport} />
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="px-3 py-2 bg-blue-50 text-blue-600 text-sm font-bold rounded-lg flex items-center gap-2 border border-blue-100 hover:bg-blue-100 transition-colors">
                        <i className="fa-solid fa-upload"></i> Import Data
                    </button>
                    <button type="button" className="px-3 py-2 bg-slate-50 text-slate-600 text-sm font-bold rounded-lg flex items-center gap-2 border border-slate-200">
                        <i className="fa-regular fa-eye"></i> Preview
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                
                {/* 1. Key Highlights */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden h-[400px]">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#05144b] font-bold text-sm">
                            <i className="fa-regular fa-star text-orange-400"></i> Key Highlights
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">Manual Entry</span>
                    </div>
                        <div className="flex-1 overflow-y-auto space-y-2">
                            {form.highlights.map((h, idx) => (
                                <div key={idx} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-200">
                                    <label className="cursor-pointer w-8 h-8 shrink-0 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden hover:bg-slate-50 relative" title="Upload Icon">
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            className="hidden" 
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const n = [...form.highlights];
                                                    n[idx].file = file;
                                                    n[idx].iconPreview = URL.createObjectURL(file);
                                                    setForm(prev => ({ ...prev, highlights: n }));
                                                }
                                            }}
                                        />
                                        {h.iconPreview ? (
                                            <img src={h.iconPreview} className="w-full h-full object-cover" alt="Preview" />
                                        ) : (h.icon && !h.icon.startsWith('fa-')) ? (
                                            <img src={`${h.icon.startsWith('http') ? h.icon : 'http://localhost:5000' + h.icon}`} 
                                                className="w-full h-full object-cover p-1" alt="Icon" 
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                    target.parentElement?.insertAdjacentHTML('beforeend', '<i class="fa-solid fa-star text-slate-400 text-[10px]"></i>');
                                                }}
                                            />
                                        ) : (
                                            <i className={`fa-solid ${h.icon || 'fa-star'} text-slate-400 text-[10px]`}></i>
                                        )}
                                    </label>
                                    <button 
                                        type="button" 
                                        title="Select from Icon Library"
                                        onClick={() => {
                                            setLibraryTarget({ type: 'highlight', index: idx });
                                            setIsLibraryOpen(true);
                                        }}
                                        className="w-6 h-6 flex items-center justify-center rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-colors shrink-0"
                                    >
                                        <i className="fa-solid fa-book-open text-[10px]"></i>
                                    </button>
                                    <input 
                                        type="text" 
                                        className="flex-1 bg-transparent border-none px-2 py-1.5 text-[11px] text-[#05144b] outline-none"
                                        placeholder="Highlight text..."
                                        value={h.text}
                                        onChange={e => {
                                            const n = [...form.highlights];
                                            n[idx].text = e.target.value;
                                            setForm(prev => ({ ...prev, highlights: n }));
                                        }}
                                    />
                                    <button type="button" onClick={() => {
                                        setForm(prev => ({ ...prev, highlights: prev.highlights.filter((_, i) => i !== idx) }));
                                    }} className="w-6 h-6 flex items-center justify-center text-rose-400 hover:text-rose-600">
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={() => {
                                setForm(prev => ({ ...prev, highlights: [...prev.highlights, { text: '', icon: 'fa-star' }] }));
                            }} className="w-full py-1.5 border border-dashed border-slate-300 rounded-lg text-[11px] text-slate-500 font-medium hover:bg-slate-50 hover:text-[#05144b]">
                                + Add Highlight
                            </button>
                        </div>

                </div>

                {/* 2. Nearby Landmarks */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden h-[400px]">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#05144b] font-bold text-sm">
                            <i className="fa-solid fa-location-dot text-purple-500"></i> Nearby Landmarks
                            {(() => {
                                const rules = scoreSettings?.localityScoreRules || [];
                                const usedCategories = new Set();
                                let score = 0;
                                let maxScore = 0;
                                rules.forEach((rule: any) => {
                                    maxScore += rule.points;
                                    if (!usedCategories.has(rule.category)) {
                                        const hasMatch = form.nearbyPlaces.some(place => {
                                            const distance = parseFloat(place.distance) || 0;
                                            return place.category === rule.category && distance > 0 && distance <= rule.maxDistanceKm;
                                        });
                                        if (hasMatch) {
                                            score += rule.points;
                                            usedCategories.add(rule.category);
                                        }
                                    }
                                });
                                return (
                                    <span className="ml-2 text-[10px] font-black bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full border border-purple-200">
                                        Score: {score} / {maxScore}
                                    </span>
                                );
                            })()}
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">Manual Entry</span>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto">
                        <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 mb-3 px-2">
                            <span className="w-1/3">Category</span>
                            <span className="flex-1 ml-2">Landmark Name</span>
                            <span className="w-16 text-right">Distance (KM)</span>
                            <span className="w-6"></span>
                        </div>
                        <div className="space-y-2">
                            {form.nearbyPlaces.map((place, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <select
                                        className="w-1/3 bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-[12px] text-[#05144b] outline-none focus:border-blue-500 font-medium"
                                        value={place.category || availableCategories[0] || 'Landmark'}
                                        onChange={e => {
                                            const newP = [...form.nearbyPlaces];
                                            newP[idx].category = e.target.value;
                                            setForm(prev => ({ ...prev, nearbyPlaces: newP }));
                                        }}
                                    >
                                        {availableCategories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                    <input 
                                        type="text" 
                                        className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[12px] text-[#05144b] outline-none focus:border-blue-500 font-medium"
                                        placeholder="Landmark name"
                                        value={place.name}
                                        onChange={e => {
                                            const newP = [...form.nearbyPlaces];
                                            newP[idx].name = e.target.value;
                                            setForm(prev => ({ ...prev, nearbyPlaces: newP }));
                                        }}
                                    />
                                    <input 
                                        type="text" 
                                        className="w-16 bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-[12px] text-right text-[#05144b] outline-none focus:border-blue-500 font-bold"
                                        placeholder="0.0"
                                        value={place.distance}
                                        onChange={e => {
                                            const newP = [...form.nearbyPlaces];
                                            newP[idx].distance = e.target.value;
                                            setForm(prev => ({ ...prev, nearbyPlaces: newP }));
                                        }}
                                    />
                                    <button type="button" onClick={() => {
                                        setForm(prev => ({ ...prev, nearbyPlaces: prev.nearbyPlaces.filter((_, i) => i !== idx) }));
                                    }} className="w-6 h-6 flex items-center justify-center text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded">
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={() => {
                                setForm(prev => ({ ...prev, nearbyPlaces: [...prev.nearbyPlaces, { category: availableCategories[0] || 'Landmark', name: '', distance: '' }] }));
                            }} className="w-full py-1.5 border border-dashed border-slate-300 rounded-lg text-[11px] text-slate-500 font-medium hover:bg-slate-50 hover:text-[#05144b]">
                                + Add Landmark
                            </button>
                        </div>
                    </div>

                </div>

                {/* 3. Amenities */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden h-[400px]">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#05144b] font-bold text-sm">
                            <i className="fa-solid fa-layer-group text-blue-400"></i> Amenities
                            {(() => {
                                const score = selectedAmenities.reduce((acc, id) => acc + (globalAmenities.find(a => a._id === id)?.score || 0), 0);
                                const maxScoreText = scoreSettings?.amenitiesMaxScore || '50+';
                                return (
                                    <span className="ml-2 text-[10px] font-black bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full border border-blue-200">
                                        Score: {score} / {maxScoreText}
                                    </span>
                                );
                            })()}
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">Manual Entry</span>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto">
                        <div className="flex flex-wrap gap-2">
                            {globalAmenities.map(amenity => {
                                const isSelected = selectedAmenities.includes(amenity._id);
                                return (
                                    <div 
                                        key={amenity._id}
                                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors border ${isSelected ? 'bg-blue-50 border-blue-100 text-blue-600' : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'}`}
                                    >
                                        <button 
                                            type="button"
                                            onClick={() => {
                                                setSelectedAmenities(prev => 
                                                    isSelected ? prev.filter(id => id !== amenity._id) : [...prev, amenity._id]
                                                );
                                            }}
                                            className="flex items-center gap-1.5"
                                        >
                                            {form.amenityIcons?.[amenity._id] || (amenity.icon && amenity.icon.includes('/')) ? (
                                                <img 
                                                    src={form.amenityIcons?.[amenity._id] || getImageUrl(amenity.icon)} 
                                                    className="w-3.5 h-3.5 object-contain" 
                                                    alt="icon" 
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        if (target.parentElement && !target.parentElement.querySelector('.fa-check')) {
                                                            const icon = document.createElement('i');
                                                            icon.className = 'fa-solid fa-check text-blue-500';
                                                            target.parentElement.appendChild(icon);
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <i className={`fa-solid ${amenity.icon && amenity.icon !== 'undefined' ? amenity.icon : 'fa-check'}`}></i>
                                            )}
                                            {amenity.name}
                                        </button>
                                        {isSelected && (
                                            <label className="cursor-pointer ml-1 text-blue-400 hover:text-blue-600 border-l border-blue-200 pl-1.5" title="Upload custom icon for this amenity">
                                                <i className="fa-solid fa-upload text-[10px]"></i>
                                                <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        const url = URL.createObjectURL(file);
                                                        setForm(prev => ({ ...prev, amenityIcons: { ...(prev.amenityIcons || {}), [amenity._id]: url } }));
                                                        // Immediately upload it to the server and update the URL in state so it gets saved on submit
                                                        const fd = new FormData();
                                                        fd.append('images', file);
                                                        fetch(`${API_BASE}/upload`, {
                                                            method: 'POST',
                                                            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                                                            body: fd
                                                        })
                                                        .then(res => res.json())
                                                        .then(data => {
                                                            if (data.urls && data.urls[0]) {
                                                                setForm(p => ({ ...p, amenityIcons: { ...(p.amenityIcons || {}), [amenity._id]: data.urls[0] } }));
                                                            }
                                                        });
                                                    }
                                                }} />
                                            </label>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Custom Amenities */}
                        <div className="mt-4 space-y-2">
                            {form.customAmenities && form.customAmenities.map((ca, idx) => (
                                <div key={idx} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-200">
                                    <label className="cursor-pointer w-8 h-8 shrink-0 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden hover:bg-slate-50 relative" title="Upload Icon">
                                        {ca.iconPreview || (ca.icon && ca.icon !== 'undefined' && ca.icon !== '') ? (
                                            <img 
                                                src={ca.iconPreview || getImageUrl(ca.icon)} 
                                                className="w-full h-full object-contain p-1" 
                                                alt="icon" 
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                    if (target.parentElement && !target.parentElement.querySelector('.fa-cloud-arrow-up')) {
                                                        const icon = document.createElement('i');
                                                        icon.className = 'fa-solid fa-cloud-arrow-up text-slate-400 text-[10px]';
                                                        target.parentElement.appendChild(icon);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <i className="fa-solid fa-cloud-arrow-up text-slate-400 text-[10px]"></i>
                                        )}
                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                const url = URL.createObjectURL(file);
                                                const newCa = [...(form.customAmenities || [])];
                                                newCa[idx] = { ...newCa[idx], file, iconPreview: url };
                                                setForm(prev => ({ ...prev, customAmenities: newCa }));
                                            }
                                        }} />
                                    </label>
                                    <button 
                                        type="button" 
                                        title="Select from Icon Library"
                                        onClick={() => {
                                            setLibraryTarget({ type: 'customAmenity', index: idx });
                                            setIsLibraryOpen(true);
                                        }}
                                        className="w-6 h-6 flex items-center justify-center rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-colors shrink-0"
                                    >
                                        <i className="fa-solid fa-book-open text-[10px]"></i>
                                    </button>
                                    <input 
                                        type="text" 
                                        className="flex-1 bg-white border border-slate-200 rounded-md px-2 py-1.5 text-[11px] text-[#05144b] outline-none focus:border-blue-500 font-medium"
                                        placeholder="Amenity Name"
                                        value={ca.name}
                                        onChange={e => {
                                            const newCa = [...(form.customAmenities || [])];
                                            newCa[idx].name = e.target.value;
                                            setForm(prev => ({ ...prev, customAmenities: newCa }));
                                        }}
                                    />
                                    <button type="button" onClick={() => {
                                        setForm(prev => ({ ...prev, customAmenities: (prev.customAmenities || []).filter((_, i) => i !== idx) }));
                                    }} className="w-6 h-6 flex items-center justify-center text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded">
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={() => {
                                setForm(prev => ({ ...prev, customAmenities: [...(prev.customAmenities || []), { name: '', icon: '' }] }));
                            }} className="w-full py-1.5 border border-dashed border-slate-300 rounded-lg text-[11px] text-slate-500 font-medium hover:bg-slate-50 hover:text-[#05144b]">
                                + Add Custom Amenity
                            </button>
                        </div>
                    </div>

                </div>

                {/* 4. Specification */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden h-[400px]">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#05144b] font-bold text-sm">
                            <i className="fa-solid fa-gear text-amber-500"></i> Specification
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">Manual Entry</span>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto space-y-2">
                        {specifications.map((spec, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-200">
                                <label className="cursor-pointer w-8 h-8 shrink-0 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden hover:bg-slate-50 relative" title="Upload Icon">
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        className="hidden" 
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                const n = [...specifications];
                                                n[idx].file = file;
                                                n[idx].iconPreview = URL.createObjectURL(file);
                                                setSpecifications(n);
                                            }
                                        }}
                                    />
                                    {spec.iconPreview ? (
                                        <img src={spec.iconPreview} className="w-full h-full object-cover" alt="Preview" />
                                    ) : (spec.icon && !spec.icon.startsWith('fa-')) ? (
                                        <img src={`${spec.icon.startsWith('http') ? spec.icon : 'http://localhost:5000' + spec.icon}`} 
                                            className="w-full h-full object-cover" alt="Icon" 
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                                target.parentElement?.insertAdjacentHTML('beforeend', '<i class="fa-solid fa-check text-slate-400 text-[10px]"></i>');
                                            }}
                                        />
                                    ) : (
                                        <i className={`fa-solid ${spec.icon || 'fa-cloud-arrow-up'} text-slate-400 text-[10px]`}></i>
                                    )}
                                </label>
                                <button 
                                    type="button" 
                                    title="Select from Icon Library"
                                    onClick={() => {
                                        setLibraryTarget({ type: 'specification', index: idx });
                                        setIsLibraryOpen(true);
                                    }}
                                    className="w-6 h-6 flex items-center justify-center rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-colors shrink-0"
                                >
                                    <i className="fa-solid fa-book-open text-[10px]"></i>
                                </button>
                                <input 
                                    type="text" 
                                    className="w-1/3 bg-transparent border-none px-2 py-1.5 text-[11px] text-[#05144b] outline-none font-bold"
                                    placeholder="Category"
                                    value={spec.category}
                                    onChange={e => {
                                        const n = [...specifications];
                                        n[idx].category = e.target.value;
                                        setSpecifications(n);
                                    }}
                                />
                                <input 
                                    type="text" 
                                    className="flex-1 bg-transparent border-none px-2 py-1.5 text-[11px] text-[#05144b] outline-none"
                                    placeholder="Details..."
                                    value={spec.details}
                                    onChange={e => {
                                        const n = [...specifications];
                                        n[idx].details = e.target.value;
                                        setSpecifications(n);
                                    }}
                                />
                                <button type="button" onClick={() => {
                                    setSpecifications(prev => prev.filter((_, i) => i !== idx));
                                }} className="w-6 h-6 flex items-center justify-center text-rose-400 hover:text-rose-600">
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        ))}
                        <button type="button" onClick={() => {
                            setSpecifications(prev => [...prev, { category: '', details: '', icon: 'fa-check' }]);
                        }} className="w-full py-1.5 border border-dashed border-slate-300 rounded-lg text-[11px] text-slate-500 font-medium hover:bg-slate-50 hover:text-[#05144b]">
                            + Add Specification
                        </button>
                    </div>

                </div>

                {/* 5. FAQ */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden h-[400px]">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#05144b] font-bold text-sm">
                            <i className="fa-solid fa-circle-question text-purple-600"></i> FAQ
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">Manual Entry</span>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto space-y-3">
                        {form.faq.map((q, idx) => (
                            <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-3 relative group">
                                <input 
                                    type="text" 
                                    className="w-full bg-transparent border-none text-[12px] font-bold text-[#05144b] outline-none mb-1 pr-6"
                                    placeholder="Question?"
                                    value={q.question}
                                    onChange={e => {
                                        const newFaq = [...form.faq];
                                        newFaq[idx].question = e.target.value;
                                        setForm(prev => ({ ...prev, faq: newFaq }));
                                    }}
                                />
                                <textarea 
                                    className="w-full bg-transparent border-none text-[11px] text-slate-500 outline-none resize-none"
                                    placeholder="Answer..."
                                    rows={2}
                                    value={q.answer}
                                    onChange={e => {
                                        const newFaq = [...form.faq];
                                        newFaq[idx].answer = e.target.value;
                                        setForm(prev => ({ ...prev, faq: newFaq }));
                                    }}
                                ></textarea>
                                <button type="button" onClick={() => {
                                    setForm(prev => ({ ...prev, faq: prev.faq.filter((_, i) => i !== idx) }));
                                }} className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-rose-500 bg-rose-50 hover:bg-rose-100 hover:text-rose-600 rounded transition-colors" title="Delete FAQ">
                                    <i className="fa-solid fa-trash-can text-[12px]"></i>
                                </button>
                            </div>
                        ))}
                        <button type="button" onClick={() => {
                            setForm(prev => ({ ...prev, faq: [...prev.faq, { question: '', answer: '' }] }));
                        }} className="w-full py-1.5 border border-dashed border-slate-300 rounded-lg text-[11px] text-slate-500 font-medium hover:bg-slate-50 hover:text-[#05144b]">
                            + Add FAQ
                        </button>
                    </div>

                </div>

                {/* 6. Disclaimer */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden h-[400px]">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#05144b] font-bold text-sm">
                            <i className="fa-solid fa-triangle-exclamation text-rose-500"></i> Disclaimer
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">Manual Entry</span>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-2 bg-slate-50 p-1.5 rounded-lg border border-slate-200">
                            <button type="button" className="w-6 h-6 flex items-center justify-center text-slate-600 hover:bg-white rounded"><i className="fa-solid fa-bold text-[10px]"></i></button>
                            <button type="button" className="w-6 h-6 flex items-center justify-center text-slate-600 hover:bg-white rounded"><i className="fa-solid fa-italic text-[10px]"></i></button>
                            <button type="button" className="w-6 h-6 flex items-center justify-center text-slate-600 hover:bg-white rounded"><i className="fa-solid fa-underline text-[10px]"></i></button>
                            <div className="w-px h-4 bg-slate-300 mx-1"></div>
                            <button type="button" className="w-6 h-6 flex items-center justify-center text-slate-600 hover:bg-white rounded"><i className="fa-solid fa-list-ul text-[10px]"></i></button>
                        </div>
                        <textarea 
                            className="w-full flex-1 bg-slate-50 border border-slate-200 rounded-xl p-3 text-[11px] text-[#05144b] outline-none focus:border-blue-500 resize-none leading-relaxed"
                            placeholder="Enter disclaimer text..."
                            value={form.disclaimer}
                            name="disclaimer"
                            onChange={handleChange}
                        ></textarea>
                    </div>

                </div>


                {/* 8. Legal Documents */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden h-[400px]">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#05144b] font-bold text-sm">
                            <i className="fa-solid fa-file-pdf text-blue-700"></i> Legal Documents
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">Manual Entry</span>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto space-y-3">
                        {legalDocuments.map((doc, idx) => (
                            <div key={idx} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0 relative group">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-red-50 text-red-500 rounded-lg flex items-center justify-center border border-red-100">
                                        <i className="fa-solid fa-file-pdf text-lg"></i>
                                    </div>
                                    <div>
                                        <input 
                                            type="text" 
                                            className="bg-transparent border-none outline-none text-[12px] font-bold text-[#05144b] p-0 mb-0.5"
                                            placeholder="Document Title"
                                            value={doc.title}
                                            onChange={e => {
                                                const n = [...legalDocuments];
                                                n[idx].title = e.target.value;
                                                setLegalDocuments(n);
                                            }}
                                        />
                                        <p className="text-[10px] text-slate-500">{doc.file ? `${(doc.file.size / 1024 / 1024).toFixed(1)} MB` : 'PDF Document'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-slate-400">
                                    <label className="cursor-pointer hover:text-blue-500 transition-colors">
                                        <i className="fa-solid fa-upload text-sm"></i>
                                        <input type="file" accept=".pdf" className="hidden" onChange={e => {
                                            if (e.target.files && e.target.files[0]) {
                                                const n = [...legalDocuments];
                                                n[idx].file = e.target.files[0];
                                                setLegalDocuments(n);
                                            }
                                        }} />
                                    </label>
                                    <button type="button" onClick={() => {
                                        setLegalDocuments(prev => prev.filter((_, i) => i !== idx));
                                    }} className="hover:text-rose-500 transition-colors"><i className="fa-solid fa-xmark text-sm"></i></button>
                                </div>
                            </div>
                        ))}
                        <button type="button" onClick={() => {
                            setLegalDocuments(prev => [...prev, { title: '', image: '', imagePreview: '' }]);
                        }} className="w-full py-1.5 border border-dashed border-slate-300 rounded-lg text-[11px] text-slate-500 font-medium hover:bg-slate-50 hover:text-[#05144b]">
                            + Add Document
                        </button>
                    </div>

                </div>

            </div>
            <IconLibraryModal 
                isOpen={isLibraryOpen} 
                onClose={() => setIsLibraryOpen(false)} 
                onSelect={handleLibrarySelect} 
            />
        </div>
    );
};

export default Step8Grid;
